import React from 'react';

import {enumValues, hasValue} from 'web-app/util/typescript';
import {
    getNavigationLinkRouteConfig,
    type NavigationLinkRouteConfig,
    NavigationRouteKey,
} from 'web-app/react/navigation-v2/config/navigation-link';
import * as Types from 'web-app/react/navigation-v2/types';
import {doPathsMatch} from 'web-app/react/navigation-v2/helpers';

import {NavigationLinkIds} from '../types';

type Path = string;
type PathToNavigationLinkId<T> = {[k: Path]: T | undefined};

/**
 * This will create an object with paths as keys and NavigationLinkIds as values.
 *
 * @private This is only exported for unit testing
 */
export function _createPathToLinkIdMap<T extends {}>(
    // The last two values are added to they can be stubbed for testing
    navigationLinkIds: T,
    lookupRouteConfigFromID: (linkID: T[keyof T]) => NavigationLinkRouteConfig,
): PathToNavigationLinkId<T[keyof T]> {
    return enumValues(navigationLinkIds).reduce((reduction, navigationLinkId) => {
        // Look up the RouteConfig for the NavigationLinkId
        const routeConfig = lookupRouteConfigFromID(navigationLinkId);

        // Only look up using NavigationRouteKey in the routeConfig to filter out possible bad entries
        // Normalise the structure of RouteConfig to get a flat list of paths
        const paths = enumValues(NavigationRouteKey)
            .map(routeConfigKey => routeConfig[routeConfigKey])
            .filter(hasValue)
            .flatMap(routeConfig =>
                routeConfig instanceof Array ? routeConfig.map(route => route.path) : [routeConfig.path],
            );

        // Add paths to reduction
        for (let i = 0; i < paths.length; i++) {
            // Since we're reducing a large array with a lot of values, we have to keep performance in mind,
            // and this is just way more performant than spreading over the reduction
            reduction[paths[i]] = navigationLinkId;
        }

        return reduction;
    }, {} as PathToNavigationLinkId<T[keyof T]>);
}

/**
 * Example of what `pathToNavigationLinkIdMap` might look like:
 *
 * {
 *     "/account/institution/:institutionId": MockAPITypes.NavigationLinkIds.INSTITUTION_OVERVIEW,
 *     "/account/settings": MockAPITypes.NavigationLinkIds.SETTINGS,
 *     ...
 * }
 */
const pathToNavigationLinkIdMap = _createPathToLinkIdMap(Types.NavigationLinkIds, getNavigationLinkRouteConfig);

/**
 * Helper method for getting the NavigationLinkId from a hash string
 * @param hash The hash to get the navigationLinkId from. It should always start with '#/'.
 * @returns NavigationLinkId (if found) or null (otherwise)
 */
const getNavigationLinkIdFromUrl = (
    hash: string,
): {navigationLinkId: Types.NavigationLinkIds | null; isNoAccess: boolean} => {
    const currentPath = hash.split('#')[1];
    const currentPathWithoutQueryParams = currentPath?.split('?')[0];

    if (!currentPathWithoutQueryParams) {
        return {navigationLinkId: null, isNoAccess: false};
    }

    const pathFound = Object.keys(pathToNavigationLinkIdMap).find(targetPath => {
        return doPathsMatch(currentPathWithoutQueryParams, targetPath);
    });

    const queryParamsSet = currentPath.split('?')[1];
    const queryParams = new URLSearchParams(queryParamsSet);

    // if a user tries to access a feature to which he has no access to
    // when clicking in the sidebar menu or in the institutional picker dropdown
    // he will be presented with a No Access Page.

    // We want to ensure that and sidebar menu, and other navigation indicators
    // in the app, reflect the feature that the user is trying to access.
    // As such we need to return the navigationLinkId that is attached to the url, as a query param
    // with the key of noAccess

    // "/account/institution/{institutionId}/noAccess?navigationLinkId={previousLocation}"
    // i.e. "/account/institution/{institutionId}/noAccess?navigationLinkId=HEAD_COUNT"
    // would direct the user to No Access, when trying to access the HEAD_COUNT feature, when he has no access

    const navigationLinkId = pathFound ? pathToNavigationLinkIdMap[pathFound] ?? null : null;

    if (navigationLinkId === NavigationLinkIds.NO_ACCESS) {
        // Grab the `navigationLinkId` from the query params.
        const navigationLinkId = queryParams.get('navigationLinkId');

        if (!navigationLinkId) {
            return {navigationLinkId: null, isNoAccess: false};
        }

        return {navigationLinkId: NavigationLinkIds[navigationLinkId], isNoAccess: true};
    } else {
        // We're not on the noAccess route, let's just return the `navigationLinkId`
        return {navigationLinkId, isNoAccess: false};
    }
};

/**
 * This hook listens to the current path in the URL (e.g. `#/account/settings`),
 * and returns the `NavigationLinkId` (`MockAPITypes.NavigationLinkIds`)
 * that links to that path (e.g. `MockAPITypes.NavigationLinkIds.SETTINGS`).
 */
const useGetCurrentPageNavigationLinkId = () => {
    return useGetCurrentPageNavigationWithNoAccessLinkId().currentPageNavigationLinkId;
};

export default useGetCurrentPageNavigationLinkId;

/**
 * This hook listens to the current path in the URL (e.g. `#/account/settings`),
 * and returns the `NavigationLinkId` (`MockAPITypes.NavigationLinkIds`) and a boolean indicating if we're on the NoAccess page.
 */
export const useGetCurrentPageNavigationWithNoAccessLinkId = () => {
    const [data, setCurrentPageNavigationLinkId] = React.useState<{
        currentPageNavigationLinkId: Types.NavigationLinkIds | null;
        isNoAccess: boolean;
    }>(() => {
        const navigationData = getNavigationLinkIdFromUrl(window.location.hash);
        return {
            currentPageNavigationLinkId: navigationData.navigationLinkId,
            isNoAccess: navigationData.isNoAccess,
        };
    });

    React.useEffect(() => {
        const hashChangeEventListener = () => {
            const navigationData = getNavigationLinkIdFromUrl(window.location.hash);
            return setCurrentPageNavigationLinkId({
                currentPageNavigationLinkId: navigationData.navigationLinkId,
                isNoAccess: navigationData.isNoAccess,
            });
        };

        window.addEventListener('hashchange', hashChangeEventListener);

        return () => {
            window.removeEventListener('hashchange', hashChangeEventListener);
        };
    }, []);

    return {currentPageNavigationLinkId: data.currentPageNavigationLinkId, isNoAccess: data.isNoAccess};
};
